import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Phone, Trash2, ArrowRight, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  New: "text-[#C9963F] bg-[#C9963F]/10 border-[#C9963F]/40",
  Contacted: "text-blue-300 bg-blue-400/10 border-blue-400/30",
  Converted: "text-green-300 bg-green-400/10 border-green-400/30",
  Closed: "text-gray-300 bg-gray-400/10 border-gray-400/30",
};
const STATUSES = ["New", "Contacted", "Converted", "Closed"];

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.New;
  return <span className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${cls}`}>{status}</span>;
}

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return s; }
}

export default function Inquiries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/admin/inquiries"); setRows(data); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function setStatus(id, status) {
    try { await api.patch(`/admin/inquiries/${id}`, { status }); toast.success(`Set to ${status}`); load(); }
    catch { toast.error("Failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/admin/inquiries/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  }

  async function convertToQuote(id) {
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/convert-to-quote`);
      toast.success("Quote created from inquiry");
      navigate(`/admin/quotes/${data.quote_id}`);
    } catch { toast.error("Failed to convert"); }
  }

  const newCount = rows.filter((r) => r.status === "New").length;

  return (
    <div data-testid="inquiries-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Pre-sales</div>
        <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Inquiries</h1>
        <p className="text-white/50 mt-2 text-sm">{rows.length} total · {newCount} new</p>
      </div>

      {loading && <div className="text-white/40 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
          No inquiries yet. Share your landing page link to start collecting leads.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((inq) => (
          <div key={inq.id} className="bg-[#0e0e0e] border border-white/10 p-5 sm:p-6" data-testid={`inquiry-${inq.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="text-white font-medium text-lg">{inq.name}</h3>
                  <StatusBadge status={inq.status} />
                  <span className="text-[10px] uppercase tracking-wider text-white/40">{fmt(inq.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/60 mb-3">
                  {inq.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} /> <a href={`tel:${inq.phone}`} className="hover:text-[#C9963F]">{inq.phone}</a></span>}
                  {inq.email && <span className="inline-flex items-center gap-1.5"><Mail size={12} /> <a href={`mailto:${inq.email}`} className="hover:text-[#C9963F]">{inq.email}</a></span>}
                  <span>📅 {fmtDate(inq.event_date)}{inq.event_time ? ` · ${inq.event_time}` : ""}</span>
                </div>
                {inq.selected_items?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Items selected ({inq.selected_items.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {inq.selected_items.map((n, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 border border-[#C9963F]/30 text-[#C9963F]/90">{n}</span>
                      ))}
                    </div>
                  </div>
                )}
                {inq.notes && (
                  <div className="mt-3 flex items-start gap-2 text-sm text-white/70">
                    <MessageSquare size={14} className="mt-0.5 text-white/40 shrink-0" />
                    <span className="whitespace-pre-wrap">{inq.notes}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => convertToQuote(inq.id)} data-testid={`convert-${inq.id}`}
                  className="inline-flex items-center justify-center gap-1.5 bg-[#C9963F] text-black px-3 py-2 text-xs font-medium hover:bg-[#A67A2D]">
                  <Sparkles size={12} /> Create Quote <ArrowRight size={12} />
                </button>
                <div className="flex flex-wrap gap-1">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(inq.id, s)} data-testid={`status-${inq.id}-${s}`}
                      className={`px-2 py-1 text-[10px] uppercase tracking-wider border transition-colors ${
                        inq.status === s ? "border-[#C9963F] text-[#C9963F]" : "border-white/15 text-white/60 hover:border-white/40"
                      }`}>{s}</button>
                  ))}
                </div>
                <button onClick={() => remove(inq.id)} className="text-[10px] uppercase tracking-wider text-red-400/70 hover:text-red-400 inline-flex items-center justify-center gap-1 border border-red-500/20 hover:border-red-500/40 px-2 py-1">
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
